import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";

import Navbar from "@/components/Navbar";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Hero from "@/components/sections/Hero";
import TrustStrip from "@/components/sections/TrustStrip";
import AboutFounder from "@/components/sections/AboutFounder";
import Qualifications from "@/components/sections/Qualifications";
import AboutBrand from "@/components/sections/AboutBrand";
import Philosophy from "@/components/sections/Philosophy";
import Specializations from "@/components/sections/Specializations";
import Services from "@/components/sections/Services";
import Benefits from "@/components/sections/Benefits";
import WhyChoose from "@/components/sections/WhyChoose";
import GoogleReviews from "@/components/sections/GoogleReviews";import Location from "@/components/sections/Location";
import BrandMarquee from "@/components/sections/BrandMarquee";
import FAQ from "@/components/sections/FAQ";
import Consult from "@/components/sections/Consult";
import Footer from "@/components/sections/Footer";

import Seo from "@/seo/Seo";
import {
  organizationSchema,
  personSchema,
  localBusinessSchema,
  websiteSchema,
} from "@/seo/schema";

const ServicePage = lazy(() => import("@/pages/ServicePage"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const LocationsIndex = lazy(() => import("@/pages/LocationsIndex"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#050505]" aria-hidden="true" />
);

// Normalize trailing slashes to the canonical (non-trailing) URL.
// Platform static hosting cannot issue a real 301, so this is a client-side
// replace redirect that keeps the address bar and canonical consistent.
const TrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname !== "/" && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);
  return null;
};

const Landing = () => (
  <div className="App relative" data-testid="landing-root">
    <Seo
      title="Personal Trainer Dubai | Corrective Exercise & Strength | Corrective Motion"
      description="Personal trainer in Dubai for corrective exercise, strength, weight loss and pain-free movement. Train 1:1 with Anoop Gopi at Dubai Mall Zabeel or online."
      path="/"
      keywords="personal trainer dubai, personal trainer in dubai, personal trainer dubai mall, corrective exercise specialist dubai, corrective exercise dubai, weight loss personal trainer dubai, strength and conditioning coach dubai, injury prevention training dubai, posture correction dubai, mobility training dubai, online personal trainer dubai, home personal trainer dubai"
      schema={[organizationSchema, personSchema, localBusinessSchema, websiteSchema]}
    />
    <Navbar />
    <main>
      <Hero />
      <TrustStrip />
      <AboutFounder />
      <Qualifications />
      <AboutBrand />
      <Philosophy />
      <Specializations />
      <Services />
      <Benefits />
      <WhyChoose />
      <BrandMarquee />
      <GoogleReviews />
      <Location />
      <FAQ />
      <Consult />
    </main>
    <Footer />
    <WhatsAppFloat />
    <Toaster
      theme="dark"
      position="bottom-center"
      toastOptions={{
        style: {
          background: "rgba(10,10,12,0.95)",
          border: "1px solid rgba(0,229,255,0.25)",
          color: "#fff",
          backdropFilter: "blur(20px)",
        },
      }}
    />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <TrailingSlashRedirect />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* Location pages */}
          <Route path="/locations" element={<LocationsIndex />} />
          <Route path="/locations/:slug" element={<LocationPage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Admin */}
          <Route path="/admin" element={<AdminPage />} />
          {/* Service pages – top-level slugs for SEO. Must be before the catch-all. */}
          <Route path="/:slug" element={<ServicePage />} />
          {/* Branded 404 for anything unmatched */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
